import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Edit, Mail, MapPin } from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Card, CardContent, Badge } from '@components/Card';
import { SimpleTable as Table, TableHeader, TableRow, TableCell } from '@components/Table';
import { PermissionGuard } from '@components/PermissionGuard';
import eventService from '@services/eventService';
import { Event } from '@/types/event';

// Formata "YYYY-MM-DD" como dd/MM/aaaa lendo a string (nunca via new Date — fuso).
const formatDateBR = (iso?: string) => {
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
};

export const EventListView: React.FC = () => {
  const navigate = useNavigate();

  const [items, setItems] = useState<Event[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 20;

  const loadItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await eventService.listEvents(currentPage, pageSize, search.trim() || undefined);
      setItems(result.data);
      setTotalPages(result.pagination.totalPages);
      setTotalItems(result.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar eventos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-h1">Eventos</h1>
          <p className="text-slate-600">Cadastre eventos e gerencie o envio dos convites</p>
        </div>
        <PermissionGuard permission="events.write">
          <Button onClick={() => navigate('/events/new')} icon={<Plus size={20} />}>
            Novo Evento
          </Button>
        </PermissionGuard>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex gap-2">
            <Input
              placeholder="Buscar por nome do evento..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { setCurrentPage(1); loadItems(); } }}
              className="flex-1"
            />
            <Button onClick={() => { setCurrentPage(1); loadItems(); }} variant="secondary" icon={<Search size={18} />}>
              Buscar
            </Button>
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>
      )}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <tr>
                <th className="text-left">ID</th>
                <th className="text-left">Evento</th>
                <th className="text-left">Período</th>
                <th className="text-left">Cidade</th>
                <th className="text-center">Status</th>
                <th className="text-center">Ações</th>
              </tr>
            </TableHeader>
            <tbody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500">Carregando...</TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500">Nenhum evento encontrado</TableCell>
                </TableRow>
              ) : (
                items.map((item) => (
                  <TableRow key={item.event_id}>
                    <TableCell className="font-mono text-sm">{item.event_id}</TableCell>
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {formatDateBR(item.start_date)}{item.end_date ? ` — ${formatDateBR(item.end_date)}` : ''}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">
                      <span className="inline-flex items-center gap-1">
                        {item.addr_city ? <><MapPin size={12} className="text-slate-400" />{item.addr_city}{item.addr_state ? `/${item.addr_state}` : ''}</> : '—'}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant={item.is_active ? 'success' : 'neutral'}>
                        {item.is_active ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => navigate(`/events/invitations?event_id=${item.event_id}`)}
                          icon={<Mail size={16} />}
                        >
                          Convites
                        </Button>
                        <PermissionGuard permission="events.write">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => navigate(`/events/${item.event_id}/edit`)}
                            icon={<Edit size={16} />}
                          />
                        </PermissionGuard>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </tbody>
          </Table>
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-600">{totalItems} registro(s) encontrado(s)</p>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" disabled={currentPage <= 1} onClick={() => setCurrentPage((p) => p - 1)}>
              Anterior
            </Button>
            <span className="flex items-center px-3 text-sm text-slate-600">Página {currentPage} de {totalPages}</span>
            <Button variant="secondary" size="sm" disabled={currentPage >= totalPages} onClick={() => setCurrentPage((p) => p + 1)}>
              Próxima
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
